import React from 'react';
import { StatsCard } from './StatsCard';
import {
    Home,
    Users,
    Globe,
    ClipboardList
} from 'lucide-react';

interface SummaryGridProps {
    theme?: 'light' | 'dark';
    staggerStart?: number;
    propertiesPublished: number;
    propertiesMax: number;
    propertiesTotal: number;
    sharedProperties: number;
    sharedRequirements: number;
    activeLeads: number;
    activeRequirements: number;
}

// Nudge/reward thresholds — keep identical to the mobile dashboard
// (mobile/app/(app)/(tabs)/index.tsx statBanner helper).
export const PUBLISHED_GOAL = 20;
export const SHARED_GOAL = 5;

type Banner = { tone: 'positive' | 'nudge'; text: string };

export const publishedBanner = (published: number): Banner =>
    published < PUBLISHED_GOAL
        ? { tone: 'nudge', text: 'Entre más propiedades publicadas, más probabilidades de generar prospectos.' }
        : { tone: 'positive', text: '¡Buen ritmo! Tu inventario publicado te da visibilidad constante.' };

export const sharedPropsBanner = (shared: number): Banner =>
    shared < SHARED_GOAL
        ? { tone: 'nudge', text: `Comparte al menos ${SHARED_GOAL} propiedades para que toda la red las ofrezca a sus clientes.` }
        : { tone: 'positive', text: '¡Excelente! Toda la red puede ofrecer tus propiedades.' };

export const sharedReqsBanner = (shared: number): Banner =>
    shared < SHARED_GOAL
        ? { tone: 'nudge', text: 'Comparte tus requerimientos y deja que la red encuentre la propiedad por ti.' }
        : { tone: 'positive', text: '¡Muy bien! La red está buscando propiedades para tus clientes.' };

export const leadsBanner = (leads: number): Banner =>
    leads === 0
        ? { tone: 'nudge', text: 'Registra tus prospectos para dar seguimiento y no perder oportunidades.' }
        : { tone: 'positive', text: '¡Sigue así! El seguimiento puntual es lo que cierra ventas.' };

export const SummaryGrid: React.FC<SummaryGridProps> = ({
    theme = 'dark',
    staggerStart = 0,
    propertiesPublished,
    propertiesMax,
    propertiesTotal,
    sharedProperties,
    sharedRequirements,
    activeLeads,
}) => {
    const stats = [
        {
            label: "Propiedades Publicadas",
            value: `${propertiesPublished}/${propertiesMax}`,
            sublabel: `${propertiesTotal} en cartera`,
            progress: propertiesMax > 0 ? Math.min(propertiesPublished / propertiesMax, 1) : 0,
            icon: Home,
            color: "indigo" as const,
            banner: publishedBanner(propertiesPublished),
        },
        {
            label: "Propiedades Compartidas",
            value: sharedProperties.toString(),
            sublabel: sharedProperties < SHARED_GOAL ? `Meta: ${SHARED_GOAL} compartidas` : '¡Meta cumplida!',
            progress: Math.min(sharedProperties / SHARED_GOAL, 1),
            progressTone: 'goal' as const,
            icon: Globe,
            color: "emerald" as const,
            banner: sharedPropsBanner(sharedProperties),
        },
        {
            label: "Requerimientos Compartidos",
            value: sharedRequirements.toString(),
            sublabel: sharedRequirements < SHARED_GOAL ? `Meta: ${SHARED_GOAL} compartidos` : '¡Meta cumplida!',
            progress: Math.min(sharedRequirements / SHARED_GOAL, 1),
            progressTone: 'goal' as const,
            icon: ClipboardList,
            color: "rose" as const,
            banner: sharedReqsBanner(sharedRequirements),
        },
        {
            label: "Prospectos Activos",
            value: activeLeads.toString(),
            icon: Users,
            color: "amber" as const,
            banner: leadsBanner(activeLeads),
        },
    ];

    return (
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-8 sm:mb-10">
            {stats.map((stat, idx) => (
                <div
                    key={stat.label}
                    className="stagger-item"
                    style={{ '--i': staggerStart + idx } as React.CSSProperties}
                >
                    <StatsCard {...stat} theme={theme} />
                </div>
            ))}
        </div>
    );
};
